import * as React from "react"
import axios from "axios"
import queryString from "query-string"

import "./app.scss"

import MessageContainer from "./components/MessageContainer"
import { useSelector, useDispatch } from "react-redux"
import * as AppTypes from "AppTypes";
import { appActions, userInfoActions } from "./actions"
import { Dispatch } from "redux"
import ViewRouter from "./Routes";
import { IUserInfo } from "./types/appDataTypes"
import apiMiddleware from "./utils/api"
import { TSteps } from "./reducers/appReducers"
import { IAppConfig, TLanguage } from "./reducers/configReducers"

import { retrieveDocuments } from "./utils/retrieveDocuments"
import { retrieveLangPack, retrieveTNCData } from "./utils"
import { updateLanguageAATagging, updateAATagging } from "./utils/aatagging"
import { detectBrowser } from "./utils/detection"

const parsedSearch = queryString.parse(window.location.search) as {
    country: IUserInfo["activeCountry"]
    verify: string
    jumper: TSteps
    lang: IUserInfo["lang"]
    s_cid: string
};
if (parsedSearch.verify) {
    window.sessionStorage.removeItem("application-store")
}

const getConfig = async (dispatch: Dispatch, callBack: () => void, store?: AppTypes.ReducerState) => {
    const lang = store.userInfo.lang
        || (parsedSearch.lang && parsedSearch.lang.toUpperCase() as TLanguage)
        || "EN"
    const country = store.userInfo.activeCountry
        || (parsedSearch.country && parsedSearch.country.toUpperCase() as IUserInfo["activeCountry"])
        || "SG"
    await Promise.all([
        axios.get<IAppConfig>("data/appConfig.json").then(response => {
            if (response.data) {
                // console.log(response.data)
                dispatch(appActions.updateAppConfig(response.data))
                apiMiddleware.init(response.data, country)
            }
        }),
        retrieveLangPack(lang, country),
        retrieveTNCData(country)
    ])
    if (callBack) callBack()
}

const App: React.FC<{}> = () => {
    const dispatch = useDispatch()
    const [configLoaded, setConfigLoaded] = React.useState(false)

    const { currentStep, store } = useSelector((store: AppTypes.ReducerState) => ({
        currentStep: store.app.step,
        store: store
    }))

    React.useEffect(() => {
        if (
            (window.location.hostname === "localhost" || /s-wsmsit\.sg\.uobnet\.com/.test(window.location.hostname))
            && parsedSearch.jumper
        ) {
            dispatch(appActions.gotoStep(parsedSearch.jumper as TSteps))
        }
        // eslint-disable-next-line
    }, [])

    React.useEffect(() => {
        if (parsedSearch.verify) {
            if (parsedSearch.lang) retrieveLangPack(parsedSearch.lang.toUpperCase() as TLanguage)
            dispatch(appActions.gotoStep("Verify"))
        }

        dispatch(userInfoActions.updateUserInfo(
            {
                activeCountry: store.userInfo.activeCountry
                    || (parsedSearch.country && parsedSearch.country.toUpperCase()) as IUserInfo['activeCountry']
                    || "SG"
            }
        ))

        if (parsedSearch.s_cid) {
            dispatch(userInfoActions.updateUserInfo({ s_cid: parsedSearch.s_cid }))
        }
        // window.history.replaceState({}, document.title, window.location.pathname);

        // eslint-disable-next-line
    }, [dispatch])

    React.useEffect(() => {
        getConfig(dispatch, () => {
            setConfigLoaded(true)
            retrieveDocuments(store.userInfo)
        }, store)
        // eslint-disable-next-line
    }, [dispatch])

    React.useEffect(() => {
        const persistStore = {
            app: Object.assign({}, store.app, { pictureStore: {}, isCallingApi: false }),
            userInfo: Object.assign({}, store.userInfo),
            config: Object.assign({}, store.config)
        }

        const saveDataSteps: TSteps[] = ["BasicInfo", "ParentInfo", "AddressInfo", "WorkStatus", "ArtistStatus", "Dashboard", "Declaration"]
        const clearDataSteps: TSteps[] = ["Verify", "Complete"]
        // const exceptSteps: TSteps[] = ["Error", "ServiceError"]

        if (saveDataSteps.includes(persistStore.app.step)) {
            window.sessionStorage.setItem("application-store", JSON.stringify(persistStore))
        }

        if (clearDataSteps.includes(persistStore.app.step)) {
            window.sessionStorage.removeItem("application-store")
        }

    }, [store])

    const loadScript = (callback: any, pageLoad: any) => {
        const env = (window.location.hostname === "localhost" || /s-wsmsit\.sg\.uobnet\.com/.test(window.location.hostname))
            ? 'staging' : 'production';
        const script = document.createElement("script")
        script.type = "text/javascript";
        script.src = store.config.appConfig.AATagging[env];
        document.head.appendChild(script);
        script.onload = function () {
            callback();
            pageLoad();
        };
    }

    React.useEffect(() => {
        loadScript(function () {
            const script = document.createElement("script");
            script.type = "text/javascript";
            script.src = "./aatag__footer.js";
            document.body.appendChild(script);
        }, function () {
            if ((!parsedSearch.verify) && detectBrowser()) {
                // AA Tracking page load
                const AALang = updateLanguageAATagging((store.userInfo.lang
                    || (parsedSearch.lang && parsedSearch.lang.toUpperCase() as TLanguage)
                    || "EN"))
                const AACountry = store.userInfo.activeCountry
                    || (parsedSearch.country && parsedSearch.country.toUpperCase() as IUserInfo["activeCountry"])
                    || "SG"
                updateAATagging({
                    "event_name": "generic_form_start",
                    "form_name": "UOB PoY",
                    "language": AALang,
                    "country": AACountry.toLowerCase()
                }, "other_form_start")
            }

        });
    }, [])

    return (
        <React.Fragment>
            {configLoaded ? <ViewRouter view={currentStep} key={store.config.lang} /> : null}
            <MessageContainer />
            <div id="dialogContainer"></div>
        </React.Fragment>
    )
}

export default App